import HeaderProperty from "./Show/HeaderProperty";
import { sanitizeValues } from "../../models/utils";
import ContentLayout from "../UI/Layout/ContentLayout.tsx";
import { Tabs } from "../UI/Tabs/Tabs";
import usePropertyFolders from "../../hooks/use-property-folders";
import useProperties from "../../hooks/use-properties";
import { PropertyStatus, PropertyType } from "../../models/property";
import { useNavigate, useSearchParams } from "react-router-dom";
import PropertyList from "./PropertyList";
import ComptaShow from "./ComptaShow";
import { BalanceDomain } from "../../utils/constants/balances";
import {
  buildPropertyFoldersTree,
  filterPropertiesAndFoldersByStatus,
  findPropertyFolder,
} from "../../utils/properties";
import CompteurShow from "./CompteurShow";
import MaintenanceLogShow from "./MaintenanceLogShow";
import RepartitionKeysShow from "./RepartitionKeysShow";
import DocumentShowList from "../File/DocumentShowList";
import PermissionScope from "../../store/PermissionScope";

export default function PropertyFolderShow({ propertyFolderId }) {
  const {
    propertyFolders,
    updatePropertyFolder,
    deletePropertyFolder,
    isLoading,
  } = usePropertyFolders();
  const { properties, duplicateProperty, updateProperty } = useProperties();
  const nav = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const propertyFoldersTree = buildPropertyFoldersTree(
    propertyFolders,
    properties,
  );

  const propertyFolder = findPropertyFolder(
    propertyFoldersTree.property_folders,
    propertyFolderId,
  );

  if (!propertyFolder) return null;

  // Archiver un dossier archive tout son contenu : un seul onglet de biens suffit.
  const isArchived = propertyFolder.status === PropertyStatus.Archive.status;

  async function submitHandler(values) {
    await updatePropertyFolder(propertyFolder.id, sanitizeValues(values));
  }

  // Les onglets de biens changent avec le statut : on oublie l'onglet mémorisé
  // dans l'URL pour retomber sur l'onglet par défaut du nouvel état.
  async function changeStatus(status) {
    await updatePropertyFolder(propertyFolder.id, { status });
    setSearchParams((cur) => {
      const next = new URLSearchParams(cur.toString());
      next.delete("tab-prop-show");
      return next;
    });
  }

  return (
    <PermissionScope profileId={propertyFolder?.id_profile_nom_owner}>
      <ContentLayout
        header={
          propertyFolder && (
            <HeaderProperty
              property={propertyFolder}
              onDelete={async () => {
                try {
                  await deletePropertyFolder(propertyFolder.id);
                  nav("/bailleur/properties");
                } catch (error) {
                  console.error(error);
                }
              }}
              onArchive={async () => {
                try {
                  await changeStatus(PropertyStatus.Archive.status);
                } catch (error) {
                  console.error(error);
                }
              }}
              onReactivate={async () => {
                try {
                  await changeStatus(PropertyStatus.Actif.status);
                } catch (error) {
                  console.error(error);
                }
              }}
              onUpdate={submitHandler}
              onMoveToFolder={async (propertyId) => {
                try {
                  await updateProperty(propertyId, {
                    id_property_folder: propertyFolder.id,
                  });
                } catch (error) {
                  console.error(error);
                }
              }}
              onMoveFolderToFolder={async (propertyId) => {
                try {
                  await updatePropertyFolder(propertyId, {
                    id_parent: propertyFolder.id,
                  });
                } catch (error) {
                  console.error(error);
                }
              }}
              isFolder
            />
          )
        }
        body={
          <Tabs
            id="prop-show"
            defaultValue={
              isArchived ? "biens-associes" : "biens-associes-actifs"
            }
          >
            <div className="container bg-blue paddingT10 border-blue">
              <div className="content">
                <Tabs.List>
                  {isArchived ? (
                    <Tabs.Tab value="biens-associes">Biens</Tabs.Tab>
                  ) : (
                    <>
                      <Tabs.Tab value="biens-associes-actifs">
                        Biens actifs
                      </Tabs.Tab>
                      <Tabs.Tab value="biens-associes-archives">
                        Biens archivés
                      </Tabs.Tab>
                    </>
                  )}
                  <Tabs.Tab value="documents">Documents</Tabs.Tab>
                  <Tabs.Tab value="compta">Comptabilité</Tabs.Tab>
                  <Tabs.Tab value="compteurs">Compteurs</Tabs.Tab>
                  <Tabs.Tab value="maintenance">Entretien</Tabs.Tab>
                  {propertyFolder?.property_type ===
                    PropertyType.BIEN_NON_COMPTABLE && (
                    <Tabs.Tab value="parametres">Paramètres</Tabs.Tab>
                  )}
                </Tabs.List>
              </div>
            </div>
            <div className="blueSpace"></div>
            <div className="content">
              {isArchived ? (
                <Tabs.Panel value="biens-associes">
                  <PropertyList
                    propertiesAndFolders={propertyFolder}
                    onDuplicateProperty={duplicateProperty}
                    isLoading={isLoading}
                  />
                </Tabs.Panel>
              ) : (
                <>
                  <Tabs.Panel value="biens-associes-actifs">
                    <PropertyList
                      propertiesAndFolders={filterPropertiesAndFoldersByStatus(
                        propertyFolder,
                        PropertyStatus.Actif.status,
                      )}
                      onDuplicateProperty={duplicateProperty}
                      isLoading={isLoading}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel value="biens-associes-archives">
                    <PropertyList
                      propertiesAndFolders={filterPropertiesAndFoldersByStatus(
                        propertyFolder,
                        PropertyStatus.Archive.status,
                      )}
                      onDuplicateProperty={duplicateProperty}
                      isLoading={isLoading}
                    />
                  </Tabs.Panel>
                </>
              )}
              <Tabs.Panel value="documents">
                <DocumentShowList
                  propertyFolder={propertyFolder}
                  allowEdit
                  allowDelete
                />
              </Tabs.Panel>
              <Tabs.Panel value="compta">
                <ComptaShow
                  domaineBalance={BalanceDomain.FOLDER}
                  parentId={propertyFolder?.id}
                  propertyType={propertyFolder?.property_type}
                />
              </Tabs.Panel>
              <Tabs.Panel value="compteurs">
                <CompteurShow isFolder propId={propertyFolder.id} />
              </Tabs.Panel>
              <Tabs.Panel value="maintenance">
                <MaintenanceLogShow
                  domain={BalanceDomain.FOLDER}
                  parentId={propertyFolder.id}
                />
              </Tabs.Panel>
              {propertyFolder?.property_type ===
                PropertyType.BIEN_NON_COMPTABLE && (
                <Tabs.Panel value="parametres">
                  <RepartitionKeysShow propertyFolderId={propertyFolder.id} />
                </Tabs.Panel>
              )}
            </div>
          </Tabs>
        }
      />
    </PermissionScope>
  );
}
